import {ApolloQueryResult} from 'apollo-client';
import _ from 'lodash';

import {apolloClientNoCache} from '../../../apolloClient';
import {RunsDataQuery} from '../../../containers/RunsDataLoader';
import {
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables,
  BucketedRunsDeltaQueryWithoutRunInfoDocument,
  BucketedRunsDeltaQueryWithoutRunInfoQuery,
  BucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {fromJson} from '../../../util/runs';
import {buildTimePoint} from '../componentOutliers/buildPoints';
import {HistoryRecord} from '../componentOutliers/types';
import {BucketedLine} from '../types';
import {SingleQuery} from './../../../util/queryTypes';
import {BucketedQueryType, QueryVars, queryVars} from './queryVars';

type BucketedQueryResult =
  ApolloQueryResult<BucketedRunsDeltaQueryWithoutRunInfoQuery>;
type BucketedQueryPromise = Promise<BucketedQueryResult>;

type BucketedQueryRuns = NonNullable<
  NonNullable<BucketedQueryResult['data']['project']>['runs']
>;
export type BucketedQueryRunsDeltas = NonNullable<
  BucketedQueryRuns['deltas']
>['delta'];
type BucketedQueryRunsDelta = BucketedQueryRunsDeltas[number];

type GroupedBucketedQueryPromise = Promise<
  ApolloQueryResult<GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery>
>;
type GroupedBucketedQueryResult =
  ApolloQueryResult<GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery>;
type GroupedBucketedQueryRuns = NonNullable<
  NonNullable<GroupedBucketedQueryResult['data']['project']>['groupedRuns']
>;
export type GroupedBucketedQueryRunsDeltas = NonNullable<
  GroupedBucketedQueryRuns['deltas']
>['delta'];
type GroupedBucketedQueryRunWithGroupedRunConnection = Extract<
  GroupedBucketedQueryRuns,
  {
    __typename: 'GroupedRunConnection';
  }
>;
// Narrowed deltas type specifically for the GroupedRunConnection branch so array elements
// are not inferred as any when reducing over union-shaped arrays
type GroupedBucketedRunDeltas = NonNullable<
  NonNullable<GroupedBucketedQueryRunWithGroupedRunConnection['deltas']>
>['delta'];
interface BaseQueryBehavior<TRes, TRuns> {
  query: (
    vars: QueryVars,
    currentRuns: string[],
    lastUpdated: string
  ) => Promise<TRes>;
  unpack: (result: TRes) => TRuns | null | undefined;
  postProcess: (runs: TRuns | null | undefined) => void;
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: TRuns | null | undefined;
  }) => Record<string, BucketedLine>;
}

type GroupedQueryBehaviorType = BaseQueryBehavior<
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns
>;

type BucketedQueryBehaviorType = BaseQueryBehavior<
  BucketedQueryResult,
  BucketedQueryRuns
>;

function doBucketedApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): BucketedQueryPromise {
  return apolloClientNoCache.query<
    BucketedRunsDeltaQueryWithoutRunInfoQuery,
    BucketedRunsDeltaQueryWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BucketedRunsDeltaQueryWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}
function doBucketedApiQueryByInternalId(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string
): BucketedQueryPromise {
  return apolloClientNoCache.query<
    BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
    BucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function applyDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
}): Record<string, BucketedLine> {
  const newRunsById = delta.reduce((acc, runDelta) => {
    const parsedRun = fromJson(runDelta.run);
    if (parsedRun == null || runDelta.run == null) {
      console.error('Unable to parse run from server in outliers reducer');
      return acc;
    }

    acc[runDelta.run.id] = {
      ...parsedRun,
      history: runDelta.run?.bucketedHistory ?? [],
    };
    return acc;
  }, {} as Record<string, BucketedLine>);

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}

function postProcessRuns(runs: BucketedQueryRuns | null | undefined) {
  // post process data - we need to calculate absolute runtime if it might be used
  // _absolute_runtime is a derived metric that is calculated off the run's start time
  try {
    runs?.deltas?.delta?.forEach((runDelta: BucketedQueryRunsDelta) => {
      const createdAt = runDelta?.run?.createdAt ?? '';
      const startTimeInMs = new Date(createdAt).getTime();
      runDelta?.run?.bucketedHistory?.forEach((bucket: HistoryRecord[]) => {
        if (bucket.length > 0) {
          const firstPoint = bucket[0];

          if (
            '_runtimeLast' in firstPoint &&
            '_timestampLast' in firstPoint &&
            '_stepLast' in firstPoint
          ) {
            // this represents a bucket that we might need _absolute_runtime for, so we need to calculate it
            // when the data comes into the Full Fidelity Context
            bucket.forEach((point: HistoryRecord) => {
              buildTimePoint(point, {
                value: startTimeInMs,
                unit: 'milliseconds',
              });
            });
          }
        }
      });
    });
  } catch (e) {
    console.error(
      'Error creating _absolute_runtime metrics in bucketed query manager',
      e
    );
  }
}

function postProcessGroupedRuns(
  runs: GroupedBucketedQueryRunWithGroupedRunConnection | null | undefined
) {
  try {
    runs?.deltas?.delta?.forEach(runDelta => {
      runDelta?.groupedRun?.bucketedHistory?.forEach(
        (bucket: HistoryRecord[]) => {
          if (bucket.length > 0) {
            const firstPoint = bucket[0];

            if (
              '_runtimeLast' in firstPoint &&
              '_timestampLast' in firstPoint &&
              '_stepLast' in firstPoint
            ) {
              // this represents a bucket that we might need _absolute_runtime for, so we need to calculate it
              // when the data comes into the Full Fidelity Context
              bucket.forEach((point: HistoryRecord) => {
                buildTimePoint(point, {
                  value: NaN, // TODO: add createdAt to GroupedRun
                  unit: 'milliseconds',
                });
              });
            }
          }
        }
      );
    });
  } catch (e) {
    console.error(
      'Error creating _absolute_runtime metrics in bucketed query manager',
      e
    );
  }
}

const BucketedQueryBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQuery,
  unpack: (result: BucketedQueryResult) => result?.data?.project?.runs,
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

const BucketedQueryWithInternalIdBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQueryByInternalId,
  unpack: (result: BucketedQueryResult) => result?.data?.project?.runs,
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

function isAggregatedRuns(
  runs: GroupedBucketedQueryRuns | null | undefined
): runs is GroupedBucketedQueryRunWithGroupedRunConnection {
  return runs?.__typename === 'GroupedRunConnection';
}

const GroupedBucketedQueryBehavior: GroupedQueryBehaviorType = {
  query: doGroupedBucketedApiQuery,
  unpack: (result: GroupedBucketedQueryResult) =>
    result?.data?.project?.groupedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
      });
    }

    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order: runs?.deltas?.order ?? [],
      delta: runs?.deltas?.delta ?? [],
    });
  },
};

const GroupedBucketedQueryWithInternalIdBehavior: GroupedQueryBehaviorType = {
  query: doGroupedBucketedApiQueryByInternalId,
  unpack: (result: GroupedBucketedQueryResult) =>
    result?.data?.project?.groupedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
      });
    }

    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order: runs?.deltas?.order ?? [],
      delta: runs?.deltas?.delta ?? [],
    });
  },
};

async function executeQueryBehavior<
  TRes,
  TRuns extends {__typename: string; totalCount?: number}
>(
  behavior: BaseQueryBehavior<TRes, TRuns>,
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  currentRunsById: Record<string, BucketedLine>
) {
  const result = await behavior.query(vars, runIds, lastUpdated);

  const runs = behavior.unpack(result);
  behavior.postProcess(runs);
  const runsById = behavior.applyDeltaFn({
    currentRunsById,
    runs,
  });
  return {
    runsById,
    isAggregated: runs?.__typename === 'GroupedRunConnection',
    totalRuns: runs?.totalCount,
  };
}

export async function bucketedQuery({
  currentRunsById,
  expressions,
  nBuckets,
  queryType,
  runsDataQuery,
  singleQuery,
}: {
  currentRunsById: Record<string, BucketedLine>;
  expressions: string[] | undefined;
  nBuckets: number;
  queryType: BucketedQueryType;
  runsDataQuery: RunsDataQuery;
  singleQuery: SingleQuery;
}) {
  const runIds = Object.keys(currentRunsById);
  const lastUpdated =
    _.max(Object.values(currentRunsById).map(l => l.updatedAt)) ??
    new Date(0).toISOString();
  const vars = queryVars(
    runsDataQuery,
    singleQuery,
    nBuckets,
    queryType,
    expressions
  );

  // This is a switch statement because Typescript is too dumb to infer the type of the queryBehavior properly, even though
  // everything else is the same.
  // You'd think that this would work:
  //
  // const queryBehaviorsByType = {
  //   BucketedQuery: BucketedQueryBehavior,
  //   BucketedQueryWithInternalId: BucketedQueryWithInternalIdBehavior,
  //   GroupedBucketedQuery: GroupedBucketedQueryBehavior,
  //   GroupedBucketedQueryWithInternalId: GroupedBucketedQueryWithInternalIdBehavior,
  // }
  // const queryBehavior = queryBehaviorsByType[queryType];
  // return await executeQueryBehavior(queryBehavior, vars, runIds, lastUpdated, currentRunsById);
  //
  // But it doesn't, even though it's functionally equivalent.
  switch (queryType) {
    case 'BucketedQuery':
      return await executeQueryBehavior(
        BucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'BucketedQueryWithInternalId':
      return await executeQueryBehavior(
        BucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'GroupedBucketedQuery':
      return await executeQueryBehavior(
        GroupedBucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'GroupedBucketedQueryWithInternalId':
      return await executeQueryBehavior(
        GroupedBucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
  }
}

function doGroupedBucketedApiQuery(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string
): GroupedBucketedQueryPromise {
  return apolloClientNoCache.query<
    GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
    GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

function doGroupedBucketedApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): GroupedBucketedQueryPromise {
  return apolloClientNoCache.query<
    GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
    GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedRunDeltas;
}) {
  const newRunsById = delta.reduce((acc, runDelta) => {
    if (runDelta.__typename !== 'GroupBucketDiff') {
      console.error('Grouped Bucketed Delta Op returned without valid run');
      return acc;
    }
    if (!runDelta.groupedRun) {
      console.error('Grouped Bucketed Delta Op returned without valid run');
      return acc;
    }

    const parsedRun = fromJson(runDelta.groupedRun);
    if (parsedRun == null) {
      console.error('Unable to parse run from server in outliers reducer');
      return acc;
    }
    acc[runDelta.groupedRun.id] = {
      ...parsedRun,
      history: runDelta.groupedRun?.bucketedHistory ?? [],
    };
    return acc;
  }, {} as Record<string, BucketedLine>);

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}
